import { Modal } from 'cpool-components/general/Modal';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES, APPLICANT_ATTRIBUTIONS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuSend } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { MailPreview } from './MailPreview';
import { SelectedDocuments } from './SelectedDocuments';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { useDemandDocumentsMutation } from '~/features/leadDetails/hooks/useDemandDocumentsMutation';
import useDemandedDocuments from '~/features/leadDetails/hooks/useDemandedDocuments';
import useLead from '~/features/leadDetails/hooks/useLead';

type Props = {
    leadId: string;
    onClose: () => void;
    open: boolean;
};

// WhatsApp channel deliberately out of scope (KC-6428)
export const DemandModal: React.FC<Props> = ({ leadId, onClose, open }) => {
    const { t } = useTranslation();
    const { data: lead } = useLead(leadId);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { attribution, demandedDocuments, resetDemandedDocuments } = useDemandedDocuments();
    const { isPending, mutate: demandDocuments } = useDemandDocumentsMutation();

    const isDisabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].demandDocuments;
    const applicant2DidNotAgreeToDataProtection = lead?.antragsteller2?.dataProtectionAgreed === false;

    const sendMail = () => {
        if (attribution === APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2 && applicant2DidNotAgreeToDataProtection) {
            toast.error(t('LEAD_DETAILS_GENERAL.DATA_PROTECTION_AGREEMENT_WARNING'));
            return;
        }

        const documents = Object.values(demandedDocuments).flatMap((entry) =>
            entry.filter(({ requested }) => requested).map(({ name }) => name),
        );

        if (documents.length === 0) {
            toast.info(t('DOCUMENT_DEMANDER.ERRORS.SEND_DEMAND_MAIL_REQUESTS_EMPTY'));
            return;
        }

        // Close and reset only once the demand was actually sent, so a failure keeps the modal and the selection.
        demandDocuments(
            { attribution, documents, leadId },
            {
                onSuccess: () => {
                    resetDemandedDocuments();
                    onClose();
                },
            },
        );
    };

    return (
        <Modal
            open={open}
            onClose={isPending ? () => undefined : onClose}
            variant="sidebar"
            side="right"
            width="720px"
            title={t('DOCUMENT_DEMANDER.HEADER')}
        >
            <div className="flex min-h-full flex-col gap-4">
                <div className="flex flex-1 flex-col gap-6 rounded-md bg-surface-light p-4">
                    <MailPreview leadId={leadId} />
                    <SelectedDocuments />
                </div>
                <div className="flex justify-end gap-2 border-t border-grey-outline-light pt-4">
                    <Button
                        iconLeft={<LuCircleX className="w-4 h-4" />}
                        variant={ButtonVariant.SECONDARY}
                        disabled={isPending}
                        onClick={onClose}
                    >
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        iconLeft={<LuSend className="w-4 h-4" />}
                        disabled={isDisabled || isPending}
                        onClick={sendMail}
                    >
                        {t('DOCUMENT_DEMANDER.PREVIEW.SEND_MAIL')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
